/**
 * Button — 공유 버튼 프리미티브 (tokens.css 변수만 사용, 하드코딩 0)
 * ----------------------------------------------------------------------------
 * 보고서 §8.2의 버튼 발산(새로고침 3변형, 파일별 primary 재정의)을 흡수한다.
 * ★ 규격 = DESIGN-AI §5-2 (기준: Relationships) — 높이 32px · 13px · 모서리 6px ·
 *   보조 = 흰 면 + gray-200 테두리 + 본문색 글자, 주요 = blue-600 + semibold.
 *
 * 사용 예:
 *   <Button onClick={refresh}>새로고침</Button>                  // primary / md
 *   <Button variant="ghost" size="sm" leftIcon={<RefreshCw size={14}/>}>새로고침</Button>
 *   <Button variant="danger" onClick={remove}>삭제</Button>
 */
import React from "react";
import Spinner from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  block?: boolean;
  /** true 면 leftIcon 자리에 스피너 + disabled 강제 + 라벨 유지 (additive — API 동결과 충돌 없음) */
  loading?: boolean;
};

const SIZE: Record<ButtonSize, React.CSSProperties> = {
  sm: { padding: "0 12px", fontSize: 13, height: 32 },
  md: { padding: "0 12px", fontSize: 13, height: 32 },
  lg: { padding: "var(--space-3) var(--space-5)", fontSize: "var(--text-base)", height: 42 },
};

const VARIANT: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    background: "var(--accent)",
    color: "#fff",
    border: "1px solid var(--accent)",
  },
  secondary: {
    background: "var(--color-bg-white, #fff)",
    color: "var(--color-text-data, #464646)",
    border: "1px solid var(--color-border-outline, #dedede)",
  },
  ghost: {
    background: "var(--color-bg-white, #fff)",
    color: "var(--color-text-data, #464646)",
    border: "1px solid var(--color-border-outline, #dedede)",
  },
  danger: {
    background: "var(--danger)",
    color: "#fff",
    border: "1px solid var(--danger)",
  },
};

export default function Button({
  variant = "primary",
  size = "md",
  leftIcon,
  rightIcon,
  block = false,
  loading = false,
  disabled,
  style,
  children,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  ...rest
}: Props) {
  const [hover, setHover] = React.useState(false);
  const [focus, setFocus] = React.useState(false);
  const isDisabled = disabled || loading;

  const hoverBg: Record<ButtonVariant, string> = {
    primary: "var(--accent-hover)",
    secondary: "var(--color-bg-natural-50, #f5f7fb)",
    ghost: "var(--color-bg-natural-50, #f5f7fb)",
    danger: "var(--danger)",
  };

  return (
    <button
      {...rest}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      onMouseEnter={(e) => { setHover(true); onMouseEnter?.(e); }}
      onMouseLeave={(e) => { setHover(false); onMouseLeave?.(e); }}
      onFocus={(e) => { setFocus(true); onFocus?.(e); }}
      onBlur={(e) => { setFocus(false); onBlur?.(e); }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        width: block ? "100%" : undefined,
        borderRadius: 6,
        fontFamily: "inherit",
        fontWeight: variant === "primary" || variant === "danger" ? 600 : 400,
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.5 : 1,
        whiteSpace: "nowrap",
        transition: "background 0.14s ease, border-color 0.14s ease, box-shadow 0.14s ease",
        boxShadow: focus ? "var(--shadow-focus)" : "none",
        outline: "none",
        ...SIZE[size],
        ...VARIANT[variant],
        ...(hover && !isDisabled ? { background: hoverBg[variant] } : null),
        ...style,
      }}
    >
      {loading ? <Spinner size={size === "sm" ? 12 : 14} /> : leftIcon}
      {children}
      {rightIcon}
    </button>
  );
}
